import {CommonModule, registerLocaleData} from '@angular/common';
import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import {LOCALE_ID, NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatCardModule} from '@angular/material/card';
import {MAT_CHECKBOX_CLICK_ACTION, MatCheckboxModule} from '@angular/material/checkbox';
import {MatCommonModule, MatLineModule} from '@angular/material/core';
import {MatDialogModule} from '@angular/material/dialog';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatGridListModule} from '@angular/material/grid-list';
import {MatInputModule} from '@angular/material/input';
import {MatListModule} from '@angular/material/list';
import {MatMenuModule} from '@angular/material/menu';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatRadioModule} from '@angular/material/radio';
import {MatSelectModule} from '@angular/material/select';
import {MatSidenavModule} from '@angular/material/sidenav';
import {MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarModule} from '@angular/material/snack-bar';
import {MatTooltipModule} from '@angular/material/tooltip';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {far} from '@fortawesome/free-regular-svg-icons';
import {fas} from '@fortawesome/free-solid-svg-icons';
import {CookieService} from 'ngx-cookie-service';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './bootstrap/app.component';
import {EpaperAppComponent} from './bootstrap/epaper/app.component';
import {SsoAppComponent} from './bootstrap/sso/app.component';
import {ActivationPageComponent} from './components/activation-page/activation-page.component';
import {ConfirmationPageComponent} from './components/confirmation-page/confirmation-page.component';
import {AlertComponent} from './components/dialog/alert/alert.component';
import {LoaderComponent} from './components/dialog/loader/loader.component';
import {LostPwPageComponent} from './components/lost-pw-page/lost-pw-page.component';
import {NewEmailPageComponent} from './components/new-email-page/new-email-page.component';
import {Addc2uComponent} from './components/permissions-page/components/addc2u/addc2u.component';
import {PermissionsPageComponent} from './components/permissions-page/permissions-page.component';
import {ProfilePageComponent} from './components/profile-page/profile-page.component';
import {RegisterPageComponent} from './components/register-page/register-page.component';
import {SignInPageComponent} from './components/sign-in-page/sign-in-page.component';
import {SignOutPageComponent} from './components/sign-out-page/sign-out-page.component';
import {Conf} from './conf';
import {ApiService} from './services/api.service';
import {AppService} from './services/app.service';
import {DialogService} from './services/dialog.service';
import {AuthInterceptor} from './services/interceptors/auth.interceptor';
import {AutoLogoutInterceptor} from './services/interceptors/auto-logout.interceptor';
import {RouterParams} from './services/router-params.service';
import {RouterService} from './services/router.service';
import {StorageService} from './services/storage.service';
import {AdminPageComponent} from './components/admin-page/admin-page.component';
import {MatPaginatorIntl, MatPaginatorModule} from "@angular/material/paginator";
import {MatIconModule} from "@angular/material/icon";
import {MatTableModule} from "@angular/material/table";
import {C2cEditComponent} from './components/admin-page/components/c2c-edit/c2c-edit.component';
import {MatPaginatorIntlDe} from "./config/matPaginationDe";

registerLocaleData(localeDe, 'de-DE');

@NgModule({
  declarations: [
    AppComponent,
    SsoAppComponent,
    EpaperAppComponent,
    SignInPageComponent,
    SignOutPageComponent,
    RegisterPageComponent,
    ConfirmationPageComponent,
    ActivationPageComponent,
    LostPwPageComponent,
    PermissionsPageComponent,
    NewEmailPageComponent,
    ProfilePageComponent,
    LoaderComponent,
    AlertComponent,
    Addc2uComponent,
    AdminPageComponent,
    C2cEditComponent
  ],
  entryComponents: [
    SsoAppComponent,
    EpaperAppComponent,
    SignInPageComponent,
    SignOutPageComponent,
    RegisterPageComponent,
    ConfirmationPageComponent,
    ActivationPageComponent,
    LostPwPageComponent,
    PermissionsPageComponent,
    NewEmailPageComponent,
    ProfilePageComponent,
    LoaderComponent,
    AlertComponent,
    Addc2uComponent,
    C2cEditComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    FontAwesomeModule,
    CommonModule,
    MatCommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatCardModule,
    MatGridListModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    AppRoutingModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatMenuModule,
    MatSidenavModule,
    MatExpansionModule,
    MatListModule,
    MatLineModule,
    MatSnackBarModule,
    MatDialogModule,
    MatCheckboxModule,
    MatRadioModule,
    MatPaginatorModule,
    MatIconModule,
    MatTableModule
  ],
  providers: [
    {provide: LOCALE_ID, useValue: 'de-DE'},
    {provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: AutoLogoutInterceptor, multi: true},
    {provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: {duration: 3000, verticalPosition: 'top', panelClass: 'accent'}},
    {provide: MAT_CHECKBOX_CLICK_ACTION, useValue: 'check'},
    {provide: MatPaginatorIntl, useClass: MatPaginatorIntlDe},
    AppService,
    ApiService,
    RouterParams,
    RouterService,
    DialogService,
    CookieService,
    StorageService
  ]
})
export class AppModule {
  private location = window.location;
  private pathArray: string[];

  constructor(
    private library: FaIconLibrary
  ) {
    this.library.addIconPacks(fas, far);
  }

  public ngDoBootstrap(app) {
    this.pathArray = this.location.pathname.replace('/', '').split('/');

    let name = 'sso';
    if (this.pathArray[0] === 'anmelden') {
      if (typeof Conf.bootstrap[this.pathArray[1]] !== 'undefined') {
        name = this.pathArray[1];
      }
    }

    this.bootstrapRootComponent(app, name);
  }

  protected bootstrapRootComponent(app, name) {
    const componentElement = document.createElement(name);
    document.body.appendChild(componentElement);

    if (name === 'epaper') {
      app.bootstrap(EpaperAppComponent);
    } else {
      app.bootstrap(SsoAppComponent);
    }
  }
}
